import { isNil } from 'lodash-es';

import { AccountingSoftwareBillAttachmentDetails } from '@/features/accounting-software';
import { Instance as companyAccountingSoftwareService } from '@/features/accounting-software/services/companyAccountingSoftware';
import { NewPaymentRequest } from '@/legacy/services/company/paymentRequest';

import { Instance as CompanyFileStorageService } from '../services/companyFileStorageService';
import { Instance as fileStorageService } from '../services/fileStorageService';
import { FileAttachmentCallbacks } from '../types/FileAttachmentCallbacks';
import { UploadedFileViewModel } from '../types/UploadedFileViewModel';

/**
 * Creates callbacks for receivables attachables.
 */
function createBillAttachmentCallbacks(
  newPaymentRequest: NewPaymentRequest
): FileAttachmentCallbacks {
  /**
   * Called when a bill was selected with attachments in accounting software.
   *
   * Also groups all of the logic for attachments and uploads in a single place/
   */
  function getAttachments() {
    return companyAccountingSoftwareService.getPaymentRequestAttachments(
      newPaymentRequest.billId()
    );
  }

  /**
   * This will be called in react FileAttachmentsFragment when clicking on an accounting software
   * bill.
   */
  function viewAttachment(attachment: AccountingSoftwareBillAttachmentDetails): void {
    const accountingSoftware = companyAccountingSoftwareService.softwarePackage;
    if (isNil(accountingSoftware)) {
      return;
    }

    accountingSoftware.GetBillAttachment(attachment);
  }

  /**
   * This will be called in react FileAttachmentsFragment when clicking on file attachment viewing
   */
  async function viewUploadedFile(uploadedFile: UploadedFileViewModel): Promise<void> {
    const fileStorageDownloadLinkViewModel =
      await CompanyFileStorageService.generateDownloadFileAttachmentLink(
        uploadedFile.companyFileStorageFileId
      );

    await fileStorageService.viewFileStorageFile(fileStorageDownloadLinkViewModel);
  }

  /**
   * This will be called in react FileAttachmentsFragment when clicking on the delete button for an
   * payable attachment.
   */
  async function fileDeleted(uploadedFile: UploadedFileViewModel): Promise<boolean> {
    return fileStorageService.delete(uploadedFile.companyFileStorageFileId);
  }

  /**
   * This is called in react FileAttachmentsFragment when user selected some files to upload.
   * If this throws the FileAttachmentsFragment.tsx QueryFilesSelected will catch and show
   * error modal.
   */
  async function filesSelected(fileList: FileList): Promise<UploadedFileViewModel> {
    return fileStorageService.upload(fileList);
  }

  async function filesUploaded(uploadedFiles: Array<UploadedFileViewModel>): Promise<void> {
    /**
     * @todo Find a way to do this immutably.
     */
    // eslint-disable-next-line no-param-reassign
    newPaymentRequest.uploadedFiles = uploadedFiles;
  }

  return {
    getAttachments,
    viewAttachment,
    fileDeleted,
    filesSelected,
    viewUploadedFile,
    filesUploaded,
  };
}

export default createBillAttachmentCallbacks;
